import { useState } from "react";
import { Info, ChevronRight, ExternalLink, UserPlus } from "lucide-react";
import { Card } from "@/components/Card";
import { Text, Heading } from "@/components/Typography";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/utils/cn";
import {
  AgreeAndSignOffIcon,
  GiftIcon,
  StepCompletedIcon,
  PlatformTourIcon,
  StartTourIcon,
} from "@/components/icons/icons";
import { NormalButton } from "@/components/NormalButton";
import { ReferralModal } from "@/pages/gettingStarted/components/ReferralModal";

/* ─────────────────────────────────────────────
 *  Checklist Step
 * ───────────────────────────────────────────── */
export interface ChecklistStepProps {
  stepNumber: number;
  title: string;
  description: string;
  descriptionLine2?: string;
  completed: boolean;
  isLast?: boolean;
  actions: React.ReactNode;
  required?: boolean;
  tooltipText?: string;
}

export const ChecklistStep = ({
  stepNumber,
  title,
  description,
  descriptionLine2,
  completed,
  isLast = false,
  actions,
  required,
  tooltipText,
}: ChecklistStepProps) => (
  <div
    className={cn(
      "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-6 py-5",
      !isLast && "border-b border-theme-ftux-checklist-divider",
    )}
    style={completed ? { backgroundColor: "var(--color-ftux-step-completed-bg)" } : undefined}
  >
    {/* Left: Step indicator + text */}
    <div className="flex items-start gap-4 min-w-0 flex-1">
      {/* Step circle — top-aligned with the title heading */}
      <div className="flex-shrink-0 mt-0.5">
        {completed ? (
          <StepCompletedIcon
            size={24}
            className="text-[var(--color-confirm-completion-success-text)]"
          />
        ) : (
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-surface-card-inner text-accent-250 text-[11px] font-bold leading-none tracking-[0.06px]">
            {stepNumber}
          </div>
        )}
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Text
            as="p"
            className={cn(
              "font-semibold text-[14px] leading-5 tracking-[-0.15px]",
              completed ? "text-theme-text-muted line-through" : "text-theme-text-heading",
            )}
          >
            {title}
          </Text>
          {required && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-theme-badge-required-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-theme-badge-required-text">
              <span className="relative flex h-[7px] w-[7px] flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75" />
                <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-red-600 opacity-[0.82]" />
              </span>
              Required
            </span>
          )}
          {tooltipText && (
            <Tooltip
              content={tooltipText}
              variant="dark"
              side="bottom"
              className="max-w-xs text-xs leading-relaxed"
            >
              <button
                type="button"
                className="flex items-center text-theme-text-muted hover:text-theme-text"
              >
                <Info className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
        <Text as="p" className="text-accent-250 text-[12px] leading-[19.5px] mt-0.5">
          {description}
        </Text>
        {descriptionLine2 && (
          <Text as="p" className="text-accent-250 text-[12px] leading-[19.5px]">
            {descriptionLine2}
          </Text>
        )}
      </div>
    </div>

    {/* Right: Actions */}
    <div className="flex items-center gap-2.5 flex-shrink-0 flex-wrap pl-10 sm:pl-0">{actions}</div>
  </div>
);

/* ─────────────────────────────────────────────
 *  Progress Pill
 * ───────────────────────────────────────────── */
export const ProgressPill = ({ completedCount }: { completedCount: number }) => (
  <div className="flex items-center gap-3 h-[46px] px-5 rounded-2xl border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card flex-shrink-0">
    <Text
      as="span"
      className="text-accent-250 font-medium text-[12px] leading-4 uppercase tracking-wide"
    >
      Progress
    </Text>
    <div className="flex items-center gap-[6px]">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "w-8 h-[6px] rounded-[10px] transition-colors duration-300",
            i < completedCount ? "bg-progress-gradient" : "bg-secondary-200",
          )}
        />
      ))}
    </div>
    <Text as="span" className="text-button-bg font-bold text-[14px] leading-5 tracking-[-0.15px]">
      {completedCount}/3
    </Text>
  </div>
);

/* ─────────────────────────────────────────────
 *  Review & Sign Off Button
 * ───────────────────────────────────────────── */
export const ReviewSignOffButton = ({ onClick }: { onClick: () => void }) => (
  <NormalButton
    borderColor="border-transparent"
    bgColor=""
    textColor="text-white"
    hoverBgColor=""
    size="md"
    className="goals-new-btn inline-flex items-center justify-center h-9 px-4 rounded-[10px] text-14 font-semibold leading-5 tracking-[-0.15px] whitespace-nowrap"
    onClick={onClick}
    rightIcon={<ChevronRight size={12} />}
  >
    Review &amp; Sign Off
  </NormalButton>
);

/* ─────────────────────────────────────────────
 *  Platform Tour Card
 * ───────────────────────────────────────────── */
export const PlatformTourCard = ({ onStartTour }: { onStartTour: () => void }) => (
  <div className="platform-tour-card card-no-hover rounded-2xl p-5 flex flex-col gap-0">
    <div className="flex items-center gap-2 mb-3">
      <PlatformTourIcon size={13} className="platform-tour-card-label flex-shrink-0" />
      <Text
        as="span"
        className="platform-tour-card-label font-semibold text-[12px] leading-4 uppercase tracking-[0.6px]"
      >
        Platform Tour
      </Text>
    </div>
    <Heading
      as="h3"
      className="text-white font-semibold text-[16px] leading-4 tracking-[-0.31px] mb-3"
    >
      Take a guided tour
    </Heading>
    <Text as="p" className="text-white font-normal text-[12px] leading-[19.5px] mb-5">
      A quick step-by-step walkthrough of the key areas you will use — Personal Advisory Boards,
      Resources, Sessions etc.
    </Text>
    <div className="flex items-center justify-between gap-2 mt-auto">
      <Text
        as="span"
        className="platform-tour-card-timer text-[12px] leading-4 font-normal text-center"
      >
        About 60 seconds
      </Text>
      <NormalButton
        borderColor="border-transparent"
        bgColor=""
        textColor="text-white"
        hoverBgColor=""
        size="sm"
        leftIcon={<StartTourIcon className="text-white flex-shrink-0" />}
        className="platform-tour-card-btn h-[30px] rounded-[10px] font-semibold text-[12px] hover:opacity-90"
        onClick={onStartTour}
      >
        Start Tour
      </NormalButton>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
 *  Signed Off Badge
 * ───────────────────────────────────────────── */
export const SignedOffBadge = () => (
  <div className="inline-flex items-center justify-center gap-2 px-3 h-[38px] rounded-[10px] bg-theme-badge-signed-off-bg border border-theme-badge-signed-off-border">
    <AgreeAndSignOffIcon
      size={14}
      className="text-[var(--color-celebration-copy-text)] flex-shrink-0"
    />
    <Text
      as="span"
      className="font-medium text-[14px] leading-4 text-[var(--color-celebration-copy-text)] whitespace-nowrap"
    >
      Signed Off
    </Text>
  </div>
);

/* ─────────────────────────────────────────────
 *  Onboarding PDF actions — Open PDF → Mark as Read → Read
 *  (the expert Code of Conduct checklist; moved here from the XO Home page
 *  when that checklist became Getting Started Phase 1, step 2)
 * ───────────────────────────────────────────── */
export const OpenPdfButton = ({ onClick }: { onClick: () => void }) => (
  <NormalButton
    borderColor="border-surface-border"
    bgColor="bg-surface-card"
    textColor="text-theme-text-heading"
    hoverBgColor="hover:bg-surface-card-inner"
    size="md"
    leftIcon={<ExternalLink size={13} className="flex-shrink-0" />}
    className="h-[38px] px-[17px] rounded-[14px] font-semibold text-[14px] leading-5 tracking-[-0.15px] whitespace-nowrap"
    onClick={onClick}
  >
    Open PDF
  </NormalButton>
);

/** Gradient "Mark as Read" — shown only once the PDF has been opened. */
export const MarkAsReadButton = ({ onClick }: { onClick: () => void }) => (
  <NormalButton
    borderColor="border-transparent"
    bgColor=""
    textColor="text-white"
    hoverBgColor=""
    size="md"
    rightIcon={<ChevronRight size={14} className="flex-shrink-0" />}
    className="goals-new-btn inline-flex items-center justify-center gap-[2px] w-[140px] h-9 rounded-[10px] text-14 font-semibold leading-5 tracking-[-0.15px] whitespace-nowrap"
    onClick={onClick}
  >
    Mark as Read
  </NormalButton>
);

/** Shown once a PDF has been marked read. */
export const ReadBadge = () => (
  <div className="inline-flex items-center justify-center gap-2 w-[95px] h-9 rounded-[10px] bg-success-350 border border-success-450 dark:bg-green-900/30 dark:border-green-700/50">
    <AgreeAndSignOffIcon
      size={14}
      className="text-[var(--color-celebration-copy-text)] dark:text-emerald-400 flex-shrink-0"
    />
    <Text
      as="span"
      className="font-medium text-[14px] leading-4 text-[var(--color-celebration-copy-text)] dark:text-emerald-400 whitespace-nowrap"
    >
      Read
    </Text>
  </div>
);

/* ─────────────────────────────────────────────
 *  Invite a Colleague card — shared by the old XO Home and the Getting
 *  Started side panel.
 * ───────────────────────────────────────────── */
/**
 * "Invite a Colleague" -- refers an Expert Operator through the same referral
 * pop-up as the Getting Started "Refer three XOs" step: copy the personal XO
 * link, or enter their details and send the invitation from there. Referrals
 * made here count towards that step.
 */
export const InviteColleagueCard = () => {
  const [referOpen, setReferOpen] = useState(false);

  return (
    <Card className="font-inter card-no-hover rounded-2xl border border-surface-border bg-surface-card p-5">
      {/* Section label */}
      <div className="flex items-center gap-2 mb-3">
        <GiftIcon size={14} className="text-accent-250 flex-shrink-0" />
        <Text as="span" className="text-accent-250 font-medium text-[12px] leading-4 uppercase">
          Invite a Colleague
        </Text>
      </div>

      {/* Title */}
      <Heading
        as="h3"
        className="text-theme-text-heading font-semibold text-[18px] leading-[22px] tracking-[-0.44px] mb-2"
      >
        Know a great Expert Operator?
      </Heading>

      {/* Description */}
      <Text as="p" className="text-text-gray text-[12px] leading-[19.5px] mb-5">
        Refer them as an Expert Operator: copy your referral link or send them an invitation, and help grow the
        AchieveOS community.
      </Text>

      <button
        type="button"
        onClick={() => setReferOpen(true)}
        className="flex h-[42px] w-full items-center justify-center gap-2 rounded-[14px] bg-primary-purple text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white transition-opacity hover:opacity-90"
      >
        <UserPlus size={14} className="flex-shrink-0" />
        Invite
      </button>

      <ReferralModal open={referOpen} kind="xo" onOpenChange={setReferOpen} showProgress={false} />
    </Card>
  );
};
